import { NavLink, useLocation, useNavigate } from 'react-router'
import { useState, useRef, useEffect, useMemo } from 'react'
import {
  LayoutDashboard,
  Bot,
  Network,
  Phone,
  Play,
  History,
  BookOpen,
  Megaphone,
  FlaskConical,
  ClipboardList,
  Key,
  Webhook,
  Settings,
  BarChart3,
  LogOut,
  Wrench,
  AudioLines,
  Cable,
  ArrowRightLeft,
  ClipboardCheck,
  Gauge,
  Activity,
  PanelLeftClose,
  PanelLeft,
  ChevronsUpDown,
  Check,
  User,
  Building2,
  Plus,
  Plug,
  X,
  ChevronRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth'
import { ThemeToggle } from '@/components/ui/theme-toggle'
import { authApi, type OrgMembership } from '@/lib/api'
import { useQueryClient } from '@tanstack/react-query'

interface NavSection {
  label: string
  items: { label: string; icon: typeof LayoutDashboard; path: string }[]
}

const navSections: NavSection[] = [
  {
    label: 'Main',
    items: [
      { label: 'Dashboard', icon: LayoutDashboard, path: '/' },
      { label: 'Agents', icon: Bot, path: '/agents' },
      { label: 'Workflows', icon: Network, path: '/workflows' },
    ],
  },
  {
    label: 'Channels',
    items: [
      { label: 'Phone Numbers', icon: Phone, path: '/phone-numbers' },
      { label: 'SIP Trunks', icon: Cable, path: '/sip-trunks' },
      { label: 'Voices', icon: AudioLines, path: '/voices' },
      { label: 'Playground', icon: Play, path: '/playground' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { label: 'Calls', icon: History, path: '/calls' },
      { label: 'Reviews', icon: ClipboardCheck, path: '/reviews' },
      // { label: 'Campaigns', icon: Megaphone, path: '/campaigns' },  // Hidden until feature is ready
      { label: 'Knowledge Bases', icon: BookOpen, path: '/knowledge-bases' },
      { label: 'Latency', icon: Activity, path: '/latency' },
      { label: 'Heartbeat Agents', icon: Gauge, path: '/heartbeat-agents' },
    ],
  },
  {
    label: 'Configure',
    items: [
      { label: 'Tools', icon: Wrench, path: '/tools' },
      { label: 'Transfer Directory', icon: ArrowRightLeft, path: '/transfer-directory' },
      { label: 'Test Suites', icon: ClipboardList, path: '/test-suites' },
      { label: 'A/B Tests', icon: FlaskConical, path: '/ab-tests' },
      { label: 'Webhooks', icon: Webhook, path: '/webhooks' },
      { label: 'Benchmarks', icon: Gauge, path: '/benchmarks' },
    ],
  },
  {
    label: 'Account',
    items: [
      { label: 'Usage & Billing', icon: BarChart3, path: '/usage' },
      { label: 'API Keys', icon: Key, path: '/api-keys' },
      { label: 'Integrations', icon: Plug, path: '/settings/integrations' },
      { label: 'Workspace', icon: Building2, path: '/settings/workspace' },
      { label: 'Settings', icon: Settings, path: '/settings' },
    ],
  },
]

interface SidebarProps {
  mobileOpen?: boolean
  onMobileClose?: () => void
}

export function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user, orgs, activeOrg, switchOrg, setSession, clearSession, clearApiKey } = useAuthStore()
  const [collapsed, setCollapsed] = useState(false)
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false)
  const [newWorkspaceName, setNewWorkspaceName] = useState('')
  const [showNewWorkspace, setShowNewWorkspace] = useState(false)
  const [creating, setCreating] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const orgDropdownRef = useRef<HTMLDivElement>(null)
  const userMenuRef = useRef<HTMLDivElement>(null)

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(e.target as Node)) {
        setOrgDropdownOpen(false)
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Group orgs by hierarchy: platforms at top, children indented beneath their parent
  const groupedOrgs = useMemo(() => {
    const platforms = orgs.filter((o) => !o.parentOrgId)
    const result: { org: OrgMembership; isChild: boolean }[] = []
    for (const platform of platforms) {
      result.push({ org: platform, isChild: false })
      const children = orgs.filter((o) => o.parentOrgId === platform.id)
      for (const child of children) {
        result.push({ org: child, isChild: true })
      }
    }
    // Include any orphan children (parentOrgId set but parent not in user's org list)
    const accounted = new Set(result.map((r) => r.org.id))
    for (const org of orgs) {
      if (!accounted.has(org.id)) {
        result.push({ org, isChild: !!org.parentOrgId })
      }
    }
    return result
  }, [orgs])

  // Determine which platform the active org belongs to
  const activePlatformId = useMemo(() => {
    if (!activeOrg) return null
    if (!activeOrg.parentOrgId) return activeOrg.id
    return activeOrg.parentOrgId
  }, [activeOrg])

  async function handleSwitchOrg(org: OrgMembership) {
    try {
      await authApi.switchOrg(org.id)
      switchOrg(org)
      setOrgDropdownOpen(false)
      // Navigate to dashboard first, then invalidate queries to force refetch with new org context
      navigate('/')
      queryClient.invalidateQueries()
    } catch {
      // ignore
    }
  }

  async function handleCreateWorkspace() {
    if (!newWorkspaceName.trim() || creating) return
    setCreating(true)
    try {
      // Create as child of active platform
      const { org } = await authApi.createWorkspace(newWorkspaceName.trim(), activePlatformId ?? undefined)
      const updatedOrgs = [...orgs, org]
      setSession(user!, updatedOrgs, org)
      setNewWorkspaceName('')
      setShowNewWorkspace(false)
      setOrgDropdownOpen(false)
      navigate('/')
      queryClient.invalidateQueries()
    } catch {
      // ignore
    }
    setCreating(false)
  }

  async function handleSignOut() {
    try {
      await authApi.logout()
    } catch {
      // ignore
    }
    clearSession()
    clearApiKey()
    queryClient.clear()
  }

  const isSessionAuth = !!user
  // On mobile drawer, always show expanded
  const isExpanded = mobileOpen || !collapsed

  const handleNavClick = () => {
    onMobileClose?.()
  }

  // ─── Single sidebar <aside> — used by both desktop & mobile ───
  const sidebar = (
    <aside
      className={cn(
        'flex flex-col h-full bg-sidebar-bg border-r border-border transition-all duration-200 ease-out',
        mobileOpen ? 'w-[280px]' : collapsed ? 'w-16' : 'w-[260px]',
      )}
    >
      {/* Logo + Org Switcher */}
      <div className="border-b border-border">
        <div className={cn(
          'flex items-center gap-3 px-5 py-5',
          !isExpanded && 'px-0 justify-center'
        )}>
          <div className="w-8 h-8 rounded-[var(--radius-sm)] bg-accent-muted flex items-center justify-center shrink-0">
            <Bot className="w-4 h-4 text-accent" />
          </div>
          {isExpanded && (
            <div className="overflow-hidden flex-1">
              <div className="font-semibold text-sm tracking-tight text-text-primary">Relay Agent</div>
              <div className="text-[10px] text-text-muted uppercase tracking-wider">Console</div>
            </div>
          )}
          {mobileOpen && (
            <button
              onClick={onMobileClose}
              className="flex items-center justify-center w-8 h-8 rounded-[var(--radius-sm)] hover:bg-sidebar-hover transition-colors"
              aria-label="Close menu"
            >
              <X className="w-4 h-4 text-text-muted" />
            </button>
          )}
        </div>

        {/* Org switcher */}
        {isSessionAuth && isExpanded && orgs.length > 0 && (
          <div className="px-3 pb-3" ref={orgDropdownRef}>
            <button
              onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
              aria-label="Switch workspace"
              className="w-full flex items-center gap-2 px-3 py-2 rounded-[var(--radius-sm)] text-[13px] text-text-secondary hover:bg-sidebar-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Building2 className="w-3.5 h-3.5 text-text-muted shrink-0" />
              <span className="flex-1 text-left truncate">{activeOrg?.name ?? 'Select workspace'}</span>
              <ChevronsUpDown className="w-3.5 h-3.5 text-text-muted shrink-0" />
            </button>

            {orgDropdownOpen && (
              <div className="mt-1 bg-bg-base rounded-[var(--radius-sm)] border border-border-strong py-1 shadow-xl z-50 relative">
                <div className="px-3 py-1.5 text-[10px] font-medium text-text-muted uppercase tracking-wider">
                  Workspaces
                </div>
                {groupedOrgs.map(({ org, isChild }) => (
                  <button
                    key={org.id}
                    onClick={() => handleSwitchOrg(org)}
                    className={cn(
                      'w-full flex items-center gap-2 py-2 text-[13px] text-text-secondary hover:bg-sidebar-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                      isChild ? 'pl-7 pr-3' : 'px-3',
                    )}
                  >
                    {isChild && <ChevronRight className="w-3 h-3 text-text-muted shrink-0" />}
                    <span className="flex-1 text-left truncate">{org.name}</span>
                    {org.type === 'platform' && !org.parentOrgId && (
                      <span className="text-[9px] text-text-muted uppercase tracking-wider shrink-0">Platform</span>
                    )}
                    {org.id === activeOrg?.id && (
                      <Check className="w-3.5 h-3.5 text-accent shrink-0" />
                    )}
                  </button>
                ))}
                <div className="border-t border-border my-1" />
                {showNewWorkspace ? (
                  <div className="px-3 py-2 flex items-center gap-1.5">
                    <input
                      type="text"
                      placeholder="Workspace name"
                      value={newWorkspaceName}
                      onChange={(e) => setNewWorkspaceName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleCreateWorkspace()}
                      autoFocus
                      className="flex-1 bg-bg-base border border-border rounded px-2 py-1 text-xs text-text-primary placeholder:text-text-muted outline-none focus:border-accent"
                    />
                    <button
                      onClick={handleCreateWorkspace}
                      disabled={creating || !newWorkspaceName.trim()}
                      aria-label="Confirm new workspace"
                      className="text-accent hover:text-accent/80 disabled:opacity-40 p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowNewWorkspace(true)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-accent hover:bg-sidebar-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    New Workspace
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {navSections.map((section) => (
          <div key={section.label} className="mb-5">
            {isExpanded && (
              <div className="px-5 mb-2 text-[10px] font-medium text-text-muted uppercase tracking-widest">
                {section.label}
              </div>
            )}
            <div className={cn('space-y-0.5', isExpanded ? 'px-3' : 'px-2')}>
              {section.items.map((item) => {
                const matches = (p: string) =>
                  p === '/'
                    ? location.pathname === '/'
                    : location.pathname === p || location.pathname.startsWith(p + '/')
                const allPaths = navSections.flatMap((s) => s.items.map((i) => i.path))
                const hasMoreSpecific = allPaths.some(
                  (p) => p !== item.path && p.startsWith(item.path + '/') && matches(p),
                )
                const isActive = matches(item.path) && !hasMoreSpecific

                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={handleNavClick}
                    title={!isExpanded ? item.label : undefined}
                    aria-label={!isExpanded ? item.label : undefined}
                    className={cn(
                      'relative flex items-center gap-3 rounded-[var(--radius-sm)] text-[13px] font-medium transition-all duration-150',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      isExpanded ? 'px-3 py-2' : 'h-10 w-10 justify-center mx-auto',
                      isActive
                        ? 'bg-sidebar-active text-accent'
                        : 'text-text-secondary hover:text-text-primary hover:bg-sidebar-hover'
                    )}
                  >
                    {isActive && isExpanded && (
                      <div className="absolute left-0 w-[2px] h-5 bg-accent rounded-r" />
                    )}
                    <item.icon className={cn('shrink-0', isExpanded ? 'w-4 h-4' : 'w-[18px] h-[18px]')} />
                    {isExpanded && item.label}
                  </NavLink>
                )
              })}
            </div>
          </div>
        ))}

        {/* Platform admin section — only visible for platform orgs */}
        {activeOrg?.type === 'platform' && (
          <div className="mb-5">
            {isExpanded && (
              <div className="px-5 mb-2 text-[10px] font-medium text-text-muted uppercase tracking-widest">
                Platform
              </div>
            )}
            <div className={cn('space-y-0.5', isExpanded ? 'px-3' : 'px-2')}>
              <NavLink
                to="/platform"
                onClick={handleNavClick}
                title={!isExpanded ? 'Workspaces' : undefined}
                aria-label={!isExpanded ? 'Workspaces' : undefined}
                className={cn(
                  'relative flex items-center gap-3 rounded-[var(--radius-sm)] text-[13px] font-medium transition-all duration-150',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isExpanded ? 'px-3 py-2' : 'h-10 w-10 justify-center mx-auto',
                  location.pathname === '/platform'
                    ? 'bg-sidebar-active text-accent'
                    : 'text-text-secondary hover:text-text-primary hover:bg-sidebar-hover'
                )}
              >
                {location.pathname === '/platform' && isExpanded && (
                  <div className="absolute left-0 w-[2px] h-5 bg-accent rounded-r" />
                )}
                <Building2 className={cn('shrink-0', isExpanded ? 'w-4 h-4' : 'w-[18px] h-[18px]')} />
                {isExpanded && 'Workspaces'}
              </NavLink>
            </div>
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className="border-t border-border p-3 space-y-1">
        {/* User profile (session auth only) */}
        {isSessionAuth && isExpanded && (
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              aria-label="User menu"
              className="w-full flex items-center gap-3 px-3 py-2 rounded-[var(--radius-sm)] text-[13px] hover:bg-sidebar-hover transition-colors mb-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <div className="w-7 h-7 rounded-full bg-accent-muted flex items-center justify-center shrink-0">
                <User className="w-3.5 h-3.5 text-accent" />
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="text-text-primary text-[13px] font-medium truncate">{user.name}</div>
                <div className="text-text-muted text-[10px] truncate">{user.email}</div>
              </div>
            </button>

            {userMenuOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-1 bg-bg-base rounded-[var(--radius-sm)] border border-border-strong py-1 shadow-xl z-50">
                <button
                  onClick={() => { navigate('/profile'); setUserMenuOpen(false); onMobileClose?.() }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-text-secondary hover:bg-sidebar-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                >
                  <User className="w-3.5 h-3.5" />
                  Profile
                </button>
                <button
                  onClick={() => { navigate('/settings/workspace'); setUserMenuOpen(false); onMobileClose?.() }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-text-secondary hover:bg-sidebar-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                >
                  <Building2 className="w-3.5 h-3.5" />
                  Workspace Settings
                </button>
                <div className="border-t border-border my-1" />
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-error hover:bg-error-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        )}

        {/* Theme toggle */}
        {isExpanded && <ThemeToggle />}

        {/* Collapse toggle — desktop only */}
        {!mobileOpen && (
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={cn(
              'hidden md:flex items-center gap-3 w-full rounded-[var(--radius-sm)] text-[13px] text-text-muted transition-all duration-150',
              'hover:text-text-secondary hover:bg-sidebar-hover',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              collapsed ? 'h-10 justify-center' : 'px-3 py-2',
            )}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <PanelLeft className="w-4 h-4 shrink-0" />
            ) : (
              <>
                <PanelLeftClose className="w-4 h-4 shrink-0" />
                <span>Collapse</span>
              </>
            )}
          </button>
        )}

        {/* Sign out (legacy API key mode or collapsed) */}
        {(!isSessionAuth || !isExpanded) && (
          <button
            onClick={isSessionAuth ? handleSignOut : clearApiKey}
            className={cn(
              'flex items-center gap-3 w-full rounded-[var(--radius-sm)] text-[13px] text-text-muted transition-all duration-150',
              'hover:text-error hover:bg-error-muted',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              !isExpanded ? 'h-10 justify-center' : 'px-3 py-2'
            )}
            title={!isExpanded ? 'Sign out' : undefined}
            aria-label="Sign out"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            {isExpanded && 'Sign Out'}
          </button>
        )}
      </div>
    </aside>
  )

  // ─── Desktop: inline in flex layout, hidden below md ───
  // ─── Mobile: fixed overlay with backdrop when mobileOpen ───
  return (
    <>
      {/* Desktop — static in document flow */}
      <div className="hidden md:block min-h-screen">
        {sidebar}
      </div>

      {/* Mobile — fixed overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={onMobileClose}
            aria-hidden="true"
          />
          <div className="relative z-10 h-full w-[280px] animate-in slide-in-from-left duration-200">
            {sidebar}
          </div>
        </div>
      )}
    </>
  )
}
